import * as React from 'react';
import { useIntl } from 'react-intl';

import type { HubModule, UseFrameHooks } from '@xingternal/hub';
import type { Language } from '@xingternal/platform-layout-frame-config-templates';
import { v1 } from '@xingternal/platform-layout-frame-config-templates';
import { registerExperiment } from '@xingternal/splitkit';

const dataVersion = 1;

type Data = ReturnType<typeof v1>;

const CrateFrameConfigContext = React.createContext<Data | undefined>(
  undefined
);

const CrateFrameConfigProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const intl = useIntl();

  const data: Data = React.useMemo(() => {
    const translate = (id: string): string => {
      return intl.formatMessage({ id });
    };

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const lang = intl.locale as Language;

    const data = v1(translate, lang);

    // TODO: move into own module when available in the Brewery
    data.experiments.forEach(({ name, supportedVariants }) => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
      registerExperiment(name, supportedVariants as any);
    });

    return {
      url: '(from bundle)',
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      version: dataVersion as unknown as string,
      ...data,
    };
  }, [intl]);

  return (
    <CrateFrameConfigContext.Provider value={data}>
      {children}
    </CrateFrameConfigContext.Provider>
  );
};

const enhanceElement: HubModule['enhanceElement'] = (children) => {
  return <CrateFrameConfigProvider>{children}</CrateFrameConfigProvider>;
};

const getHooks: FrameConfigModuleGetHooks = () => {
  const useMetaConfig = (): MetaConfig => {
    const data = React.useContext(CrateFrameConfigContext);
    if (!data) throw new Error('CrateFrameConfigContext is missing');
    return { loading: false, data };
  };

  return { useMetaConfig };
};

type MetaConfig = {
  loading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  error?: Error;
};

type Hook = (...params: unknown[]) => unknown;

type FrameConfigModuleHooks = {
  [key: string]: Hook;
  useMetaConfig: () => MetaConfig;
};

type FrameConfigModuleGetHooks = (
  useFrameHooks: UseFrameHooks
) => FrameConfigModuleHooks;

type FrameConfigModule = HubModule &
  Required<Pick<HubModule, 'enhanceElement'>> & {
    name: 'crateFrameConfigModule';
    getHooks: FrameConfigModuleGetHooks;
  };

export const crateFrameConfigModule: FrameConfigModule = {
  name: 'crateFrameConfigModule',
  enhanceElement,
  getHooks,
};
